import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useProjects } from '../hooks/useProjects'

interface HighlightsProps {
  isMobile?: boolean
}

export function Highlights({ isMobile = false }: HighlightsProps) {
  const { projects, loading } = useProjects()
  const [gradientColor, setGradientColor] = useState('#7ec876')
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (isMobile) return
    const colors = ['#7ec876', '#4da6ff', '#ff6b6b', '#ffd93d', '#a78bfa', '#f472b6', '#34d399', '#f59e0b']
    const interval = setInterval(() => {
      setGradientColor(colors[Math.floor(Math.random() * colors.length)])
    }, 3333)
    return () => clearInterval(interval)
  }, [isMobile])

  if (loading) {
    return (
      <section id="highlights" className="py-12 md:py-20 relative">
        <div className="absolute inset-0 pointer-events-none z-0" style={{ background: 'linear-gradient(to bottom, #7ec876 0%, #7ec876 85%, #CFCFCF 100%)' }} />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[200px]">
            {[...Array(6)].map((_, i) => <div key={i} className="bg-gray-300 rounded-lg animate-pulse" />)}
          </div>
        </div>
      </section>
    )
  }

  // Primi 6 progetti - esattamente come prima
  const firstSix = [
    projects.find(p => p.slug === 'lavazza-professional'),
    projects.find(p => p.slug === 'genova-pra-skatepark'),
    projects.find(p => p.slug === 'beigua-trophy'),
    projects.find(p => p.slug === 'lavazza-bilanci'),
    projects.find(p => p.slug === 'typography-architecture'),
    projects.find(p => p.slug === 'dui-brand-system'),
  ].filter(Boolean)

  // Lista completa di tutti i progetti (12 totali)
  const allProjectSlugs = ['lavazza-professional', 'genova-pra-skatepark', 'beigua-trophy', 'typography-architecture', 'dui-brand-system', 'tribute-discovery-daft-punk', 'juventus', 'ove', 'fpt', 'distinto', 'photo', 'lavazza-bilanci']
  // Primi 6 progetti da mostrare sempre
  const firstSixSlugs = ['lavazza-professional', 'genova-pra-skatepark', 'beigua-trophy', 'typography-architecture', 'dui-brand-system', 'tribute-discovery-daft-punk']
  // Progetti rimanenti (tutti tranne i primi 6)
  const remaining = projects.filter(p => p.slug && allProjectSlugs.includes(p.slug) && !firstSixSlugs.includes(p.slug))

  return (
    <section id="highlights" className="py-12 md:py-20 relative">
      <div className="absolute inset-0 pointer-events-none z-0" style={{ background: `linear-gradient(to bottom, #7ec876 0%, ${gradientColor} 85%, #CFCFCF 100%)` }} />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[200px]">
          {/* Project 1 - Lavazza Professional (large) */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="md:col-span-2 md:row-span-2 group cursor-pointer">
            <a href="#lavazza-professional" className="h-full w-full rounded-lg overflow-hidden relative block">
              <img src="/assets/projects/LAVAZZA_PROFESSIONAL/thumb.webp" alt="Lavazza Professional" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6">
                <p className="text-xs uppercase tracking-wider text-white/80 mb-1">Graphic Design</p>
                <h3 className="text-xl font-bold text-white">Lavazza Professional</h3>
              </div>
            </a>
          </motion.div>

          {/* Project 2 - Genova Prà Skatepark */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} className="group cursor-pointer">
            <a href="#genova-pra-skatepark" className="h-full w-full rounded-lg overflow-hidden relative block">
              <video src="/assets/projects/genova-pra-skatepark/skatepark_pra.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" poster="/assets/projects/genova-pra-skatepark/Screenshot-2024-10-23-175131.png" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4">
                <p className="text-xs uppercase tracking-wider text-white/80 mb-1">3D / Game Mod</p>
                <h3 className="text-lg font-bold text-white">Genova Prà Skatepark</h3>
              </div>
            </a>
          </motion.div>

          {/* Project 3 - Beigua Trophy */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="md:col-span-2 group cursor-pointer">
            <a href="#beigua-trophy" className="h-full w-full rounded-lg overflow-hidden relative block">
              <video src="/assets/projects/beigua_trophy/BeiguaExperience.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" poster="/assets/projects/beigua_trophy/THUMBNAIL.webp" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6">
                <p className="text-xs uppercase tracking-wider text-white/80 mb-1">3D / Product</p>
                <h3 className="text-xl font-bold text-white">Beigua Trophy</h3>
              </div>
            </a>
          </motion.div>

          {/* Project 4 - Lavazza Bilanci */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="group cursor-pointer">
            <a href="#lavazza-bilanci" className="h-full w-full rounded-lg overflow-hidden relative block">
              <img src="/assets/projects/lavazza_bilanci/thumb_gif.gif" alt="Lavazza Bilanci" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4">
                <p className="text-xs uppercase tracking-wider text-white/80 mb-1">Graphic Design</p>
                <h3 className="text-lg font-bold text-white">Lavazza Bilanci</h3>
              </div>
            </a>
          </motion.div>

          {/* Project 5 - Typography */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }} className="md:col-span-2 md:row-span-2 group cursor-pointer">
            <a href="#typography-architecture" className="h-full w-full rounded-lg overflow-hidden relative block">
              <img src="/assets/projects/typography-architecture/THUMBNAIL.webp" alt="Typography Architecture" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6">
                <p className="text-xs uppercase tracking-wider text-white/80 mb-1">3D / Motion</p>
                <h3 className="text-xl font-bold text-white">Typography Architecture</h3>
              </div>
            </a>
          </motion.div>

          {/* Project 6 - DUì Brand System */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.6 }} className="group cursor-pointer md:row-span-2">
            <a href="#dui" className="h-full w-full rounded-lg overflow-hidden relative block">
              <img src="/IMG/DUI/thumb.webp" alt="DUì Brand System" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4">
                <p className="text-xs uppercase tracking-wider text-white/80 mb-1">Branding</p>
                <h3 className="text-lg font-bold text-white">DUì Brand System</h3>
              </div>
            </a>
          </motion.div>

          {/* PROGETTI AGGIUNTIVI */}
          {showAll && remaining.map((project, index) => {
            const pattern = index % 4
            const sizeClass = pattern === 0 || pattern === 3 ? 'md:col-span-2 md:row-span-2' : (pattern === 2 ? 'md:col-span-2' : '')
            const isDaftPunk = project.slug === 'tribute-discovery-daft-punk'
            
            return (
              <motion.div key={project.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className={`group cursor-pointer ${sizeClass}`}>
                <a href={`#${project.slug}`} className="h-full w-full rounded-lg overflow-hidden relative block">
                  {isDaftPunk ? (
                    <video src="/assets/projects/tribute-discovery-daft-punk/0001-0240.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" poster="/assets/projects/tribute-discovery-daft-punk/interstella5555_copertina.jpg" />
                  ) : project.thumbnail ? (
                    <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center"><span className="text-white text-sm">{project.title}</span></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4">
                    <p className="text-xs uppercase tracking-wider text-white/80 mb-1">{project.category}</p>
                    <h3 className="text-lg font-bold text-white">{project.title}</h3>
                  </div>
                </a>
              </motion.div>
            )
          })}
        </div>

        {/* View All Button */}
        {remaining.length > 0 && (
          <div className="mt-8 text-center">
            <button onClick={() => setShowAll(!showAll)} className="group relative inline-flex items-center gap-2 px-8 py-4 text-white text-base font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}>
              <span className="relative z-10 flex items-center gap-2">
                {showAll ? <><span>Show Less</span><span>↑</span></> : <><span>View All</span><span>↓</span></>}
              </span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
          </div>
        )}
        
        <style>{`
          @keyframes gradient-shift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        `}</style>
      </div>
    </section>
  )
}
